import { ENTITY, RoleLevelEnum, UserActions } from '@/enums';
import { useGetMe } from '@/hooks/useGetProfileQuery';
import { MenuKey, Route, ScopeEffectEnum } from '@/interfaces';
import { useMobile } from '@amili-v3/amili-v3-design/src/hooks/useMobile';
import { ComponentType, lazy, useCallback, useMemo } from 'react';
import { PRIVATE_ROUTES } from './routes';

const Administrator = lazy(() => import('@/pages/authenPages/Administrator'));
const Customer = lazy(() => import('@/pages/authenPages/Customer/index'));
const Email = lazy(() => import('@/pages/authenPages/Email'));
const Kit = lazy(() => import('@/pages/authenPages/Kit'));
const Product = lazy(() => import('@/pages/authenPages/Product'));
const Project = lazy(() => import('@/pages/authenPages/Project'));
const Report = lazy(() => import('@/pages/authenPages/Report'));
const Order = lazy(() => import('@/pages/authenPages/Order'));
const Extraction = lazy(() => import('@/pages/authenPages/Extraction'));
const Sequencing = lazy(() => import('@/pages/authenPages/Sequencing'));
const Analysis = lazy(() => import('@/pages/authenPages/Analysis'));
const Organization = lazy(() => import('@/pages/authenPages/Organization'));
const Dashboard = lazy(() => import('@/pages/authenPages/Dashboard'));
const Profile = lazy(() => import('@/pages/authenPages/Profile'));
const AlertQC = lazy(() => import('@/pages/authenPages/AlertQC'));
const FMT = lazy(() => import('@/pages/authenPages/FMT'));
const GodMode = lazy(() => import('@/pages/authenPages/GodMode'));

interface DashboardRouteConfig {
  component: ComponentType<any>;
  // GET on ANY of these entities unlocks the route (e.g. FMT accepts either
  // fmt-donor or fmt-recipient). Omitted entirely means always accessible.
  entities?: ENTITY[];
  // Sider nav item this route corresponds to, if it has one (e.g. SURVEY and
  // GOD_MODE are routable but not shown in the nav).
  menuKey?: MenuKey;
  superAdminOnly?: boolean;
}

// Module-view scope per nav module: `${entity}:get` must be ALLOWed to see
// the module at all (route + Sider / mobile nav item), ON TOP of the route's
// own `entities` check. PROFILE has none on purpose - it's the user's own
// account page and the fallback landing route, never locked.
const MODULE_VIEW_ENTITY: Partial<Record<MenuKey, ENTITY>> = {
  ALERT: ENTITY.ALERT_MODULE,
  DASHBOARD: ENTITY.DASHBOARD_MODULE,
  ADMINISTRATOR: ENTITY.ADMINISTRATOR_MODULE,
  CUSTOMER: ENTITY.CUSTOMER_MODULE,
  PROJECT: ENTITY.PROJECT_MODULE,
  ORDER: ENTITY.ORDER_MODULE,
  KIT: ENTITY.KIT_MODULE,
  EXTRACTION: ENTITY.EXTRACTION_MODULE,
  SEQUENCING: ENTITY.SEQUENCING_MODULE,
  ANALYSIS: ENTITY.ANALYSIS_MODULE,
  REPORT: ENTITY.REPORT_MODULE,
  FMT: ENTITY.FMT_MODULE,
  EMAIL: ENTITY.EMAIL_MODULE,
  PRODUCT: ENTITY.PRODUCT_MODULE,
  ORGANIZATION: ENTITY.ORGANIZATION_MODULE,
};

// NOTICE: The order of the routes here is important since first route with permission will be the one that landing on user if the path is root
const DESKTOP_DASHBOARD_ROUTES: Record<string, DashboardRouteConfig> = {
  [PRIVATE_ROUTES.DASHBOARD + '/*']: {
    component: Dashboard,
    entities: [ENTITY.DASHBOARD],
    menuKey: 'DASHBOARD',
  },
  [PRIVATE_ROUTES.ADMINISTRATOR.ROOT + '/*']: {
    component: Administrator,
    entities: [ENTITY.USERS],
    menuKey: 'ADMINISTRATOR',
  },
  [PRIVATE_ROUTES.CUSTOMER.ROOT + '/*']: {
    component: Customer,
    entities: [ENTITY.CUSTOMER],
    menuKey: 'CUSTOMER',
  },
  [PRIVATE_ROUTES.PROJECT.ROOT + '/*']: {
    component: Project,
    entities: [ENTITY.PROJECTS],
    menuKey: 'PROJECT',
  },
  [PRIVATE_ROUTES.KIT.ROOT + '/*']: {
    component: Kit,
    entities: [ENTITY.KITS],
    menuKey: 'KIT',
  },
  [PRIVATE_ROUTES.ALERT.ROOT + '/*']: {
    component: AlertQC,
    entities: [ENTITY.ALERT],
    menuKey: 'ALERT',
  },
  [PRIVATE_ROUTES.REPORT.ROOT + '/*']: {
    component: Report,
    entities: [ENTITY.REPORTS],
    menuKey: 'REPORT',
  },
  // [PRIVATE_ROUTES.SURVEY.ROOT + '/*']: {
  //   component: Survey,
  //   entities: [ENTITY.SURVEYS],
  // },
  [PRIVATE_ROUTES.EMAIL]: {
    component: Email,
    entities: [ENTITY.KIT_EMAIL_STATUS],
    menuKey: 'EMAIL',
  },
  [PRIVATE_ROUTES.PRODUCT.ROOT + '/*']: {
    component: Product,
    entities: [ENTITY.PRODUCTS],
    menuKey: 'PRODUCT',
  },
  [PRIVATE_ROUTES.EXTRACTION.ROOT + '/*']: {
    component: Extraction,
    entities: [ENTITY.EXTRACTION],
    menuKey: 'EXTRACTION',
  },
  [PRIVATE_ROUTES.SEQUENCING.ROOT + '/*']: {
    component: Sequencing,
    entities: [ENTITY.BATCHES],
    menuKey: 'SEQUENCING',
  },
  [PRIVATE_ROUTES.ORDER.ROOT + '/*']: {
    component: Order,
    entities: [ENTITY.ORDER],
    menuKey: 'ORDER',
  },
  [PRIVATE_ROUTES.ANALYSIS.ROOT + '/*']: {
    component: Analysis,
    entities: [ENTITY.ANALYSIS],
    menuKey: 'ANALYSIS',
  },
  [PRIVATE_ROUTES.ORGANIZATION.ROOT + '/*']: {
    component: Organization,
    entities: [ENTITY.TENANTS],
    menuKey: 'ORGANIZATION',
  },
  [PRIVATE_ROUTES.FMT.ROOT + '/*']: {
    component: FMT,
    entities: [ENTITY.FMT_DONOR, ENTITY.FMT_RECIPIENT],
    menuKey: 'FMT',
  },
  [PRIVATE_ROUTES.PROFILE.ROOT + '/*']: {
    component: Profile,
    menuKey: 'PROFILE',
  },
  // God Mode — only reachable by SUPER_ADMIN (hotkey: press "." 6 times)
  [PRIVATE_ROUTES.GOD_MODE]: {
    component: GodMode,
    superAdminOnly: true,
  },
};

const MOBILE_DASHBOARD_ROUTES: Record<string, DashboardRouteConfig> = {
  [PRIVATE_ROUTES.ORDER.ROOT + '/*']: {
    component: Order,
    entities: [ENTITY.ORDER],
    menuKey: 'ORDER',
  },
  [PRIVATE_ROUTES.PROFILE.ROOT + '/*']: {
    component: Profile,
    menuKey: 'PROFILE',
  },
};

export const useGetRoutesWithPermissions = () => {
  const isMobile = useMobile();
  const dashboardRoutes = useMemo(
    () => (isMobile ? MOBILE_DASHBOARD_ROUTES : DESKTOP_DASHBOARD_ROUTES),
    [isMobile],
  );
  const { data: loggedInUser, isLoading } = useGetMe();

  const loggedInUserMainRole = loggedInUser?.mainRole;
  const isSuperAdmin =
    loggedInUserMainRole?.level === RoleLevelEnum.SUPER_ADMIN;

  const allScopesValues = loggedInUser?.scopes ?? {};

  const canAccess = useCallback(
    (route: DashboardRouteConfig) => {
      if (isSuperAdmin) return true;
      if (route.superAdminOnly) return isSuperAdmin;

      const canGet = (entity: ENTITY) =>
        allScopesValues[`${entity}:${UserActions.GET.toLowerCase()}`]
          ?.effect === ScopeEffectEnum.ALLOW;

      const moduleViewEntity = route.menuKey
        ? MODULE_VIEW_ENTITY[route.menuKey]
        : undefined;
      if (moduleViewEntity && !canGet(moduleViewEntity)) return false;

      if (!route.entities) return true;
      return route.entities.some(canGet);
    },
    [allScopesValues, isSuperAdmin],
  );

  const routes: Route[] = useMemo(() => {
    const entries = Object.entries(dashboardRoutes);
    // While the profile is still loading, keep every route registered so
    // the current URL still matches and the caller's loading state can
    // render (see Router.tsx) instead of falling through to the catch-all
    // redirect. Permission filtering only kicks in once loaded.
    const filteredEntries = isLoading
      ? entries
      : entries.filter(([, route]) => canAccess(route));
    return filteredEntries.map(([path, route]) => ({
      path,
      component: route.component,
    }));
  }, [isLoading, canAccess, dashboardRoutes]);

  const permissions = useMemo(
    () =>
      Object.values(dashboardRoutes).reduce(
        (acc, route) => {
          if (!route.menuKey) return acc;
          let isAllowed = false;
          if (!isLoading && canAccess(route)) {
            isAllowed = true;
          }
          return {
            ...acc,
            [route.menuKey]: isAllowed,
          };
        },
        {} as Record<MenuKey, boolean>,
      ),
    [isLoading, canAccess, dashboardRoutes],
  );

  return { routes, permissions, isLoading };
};
